import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Brightness1 } from '@mui/icons-material';
import { Divider, Grid } from '@mui/material';

import { PROGRESS_ICON_COLOR } from 'commons/styles/colors';
import { IStockTransfer } from 'commons/types';
import { getStockTransferAction } from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';
import { STOCK_TRANSFER_ROUTES } from 'routes/constants';

import { StockTransferStepJourney } from './components';
import { stockTransferListStyle as S } from './StockTransferList.style';

export const StockTransferListDestinationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { stockTransfer } = useSelector(
    (state: RootReducerInterface) => state.stockTransfer,
  );

  const [isValidatedNextStep, setValidatedNextStep] = useState(false);

  const handleNextStep = (): void => {
    navigate(STOCK_TRANSFER_ROUTES.SUMMARY);
  };

  useEffect(() => {
    dispatch(getStockTransferAction());
  }, [dispatch]);

  useEffect(() => {
    if (Object.values(stockTransfer).length === 0) {
      navigate(STOCK_TRANSFER_ROUTES.LIST_SOURCE);
    }
  }, [dispatch, navigate, stockTransfer]);

  useEffect(() => {
    const stockTransferList = Object.values(stockTransfer);

    for (let i = 0; i < stockTransferList.length; i += 1) {
      const { source_bin: sourceBin, destination_bin: destinationBin } =
        stockTransferList[i];

      const sourceTotalQty = sourceBin.reduce(
        (total, item) => total + item.quantity,
        0,
      );
      const destinationTotalQty = destinationBin.reduce(
        (total, item) => total + item.quantity,
        0,
      );

      if (sourceTotalQty !== destinationTotalQty) {
        setValidatedNextStep(false);
        return;
      }
    }

    setValidatedNextStep(true);
  }, [stockTransfer]);

  const renderStockTransferData = (
    stockTransferItem: IStockTransfer,
    idx: number,
  ) => {
    const skuButtonText =
      stockTransferItem.destination_bin.length === 0 ? 'START' : 'EDIT';
    const sourceQty = stockTransferItem.source_bin.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    const destinationQty = stockTransferItem.destination_bin.reduce(
      (total, item) => total + item.quantity,
      0,
    );
    const colorProgress =
      destinationQty === 0
        ? PROGRESS_ICON_COLOR[0]
        : sourceQty === destinationQty
        ? PROGRESS_ICON_COLOR[1]
        : PROGRESS_ICON_COLOR[2];
    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Grid item xs={12}>
          <Grid container>
            <S.SKUGridWrapper container>
              <S.SKUGrid
                item
                xs={8}
                data-testid={`TextSTListDestinationSKU-${idx + 1}`}
              >
                SKU {stockTransferItem.sku_code}
                <S.ProgressSpan
                  data-testid={`TextSTListDestinationQtyProgress-${idx + 1}`}
                >
                  <Brightness1 style={{ fontSize: 11, color: colorProgress }} />{' '}
                  {destinationQty}/{sourceQty}
                </S.ProgressSpan>
              </S.SKUGrid>
              <S.SKUButtonGrid item xs={4}>
                <S.SecondaryButton
                  onClick={() => {
                    navigate(
                      STOCK_TRANSFER_ROUTES.TASK_DESTINATION.replace(
                        ':skuId',
                        stockTransferItem.sku_code,
                      ),
                    );
                  }}
                  data-testid={`BtnSTListDestinationStart-${idx + 1}`}
                >
                  {skuButtonText}
                </S.SecondaryButton>
              </S.SKUButtonGrid>
            </S.SKUGridWrapper>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ marginY: '16px' }} />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <S.ContentWrapper>
        <StockTransferStepJourney currentStep="destination" />

        {Object.values(stockTransfer).map((stockTransferItem, idx) =>
          renderStockTransferData(stockTransferItem, idx),
        )}
      </S.ContentWrapper>

      <S.FooterWrapper>
        <S.PrimaryButton
          onClick={handleNextStep}
          disabled={!isValidatedNextStep}
          data-testid="BtnSTListDestinationNext"
        >
          PROCEED TO NEXT STEP
        </S.PrimaryButton>
      </S.FooterWrapper>
    </>
  );
};
